import React from 'react';
import { isEqual, max, min, sum } from 'lodash';
import { TableSortType } from './Table';

export const tableBooleanSortParameters = <ItemType,>(
  sortType: TableSortType<ItemType> | undefined,
  sortKey: keyof ItemType,
  setSortType: React.Dispatch<React.SetStateAction<TableSortType<ItemType> | undefined>>
) => {
  return {
    sortId: sortKey,
    isSortActive: sortType?.sortId === sortKey,
    sortDirection: sortType?.direction,
    sortCallback: booleanSortCallback(sortKey, setSortType),
  };
};

export const tableStringSortParameters = <ItemType,>(
  sortType: TableSortType<ItemType> | undefined,
  sortKey: keyof ItemType,
  setSortType: React.Dispatch<React.SetStateAction<TableSortType<ItemType> | undefined>>
) => {
  return {
    sortId: sortKey,
    isSortActive: sortType?.sortId === sortKey,
    sortDirection: sortType?.direction,
    sortCallback: stringSortCallback(sortKey, setSortType),
  };
};

export const tableArrayLengthSortParameters = <ItemType,>(
  sortType: TableSortType<ItemType> | undefined,
  sortKey: keyof ItemType,
  setSortType: React.Dispatch<React.SetStateAction<TableSortType<ItemType> | undefined>>
) => {
  return {
    sortId: sortKey,
    isSortActive: sortType?.sortId === sortKey,
    sortDirection: sortType?.direction,
    sortCallback: arrayLengthSortCallback(sortKey, setSortType),
  };
};

export const tableDateSortParameters = <ItemType,>(
  sortType: TableSortType<ItemType> | undefined,
  sortKey: keyof ItemType,
  setSortType: React.Dispatch<React.SetStateAction<TableSortType<ItemType> | undefined>>
) => {
  return {
    sortId: sortKey,
    isSortActive: sortType?.sortId === sortKey,
    sortDirection: sortType?.direction,
    sortCallback: dateSortCallback(sortKey, setSortType),
  };
};

export const booleanSortCallback = <ItemType,>(
  key: keyof ItemType,
  setSortType: (value: React.SetStateAction<TableSortType<ItemType> | undefined>) => void
) => {
  return (direction: 'ascending' | 'descending') => {
    const type: TableSortType<ItemType> = {
      predicate: booleanSort<ItemType, keyof ItemType & string>(key as keyof ItemType & string),
      sortId: key,
      direction: direction,
    };
    setSortType(type);
  };
};

export const stringSortCallback = <ItemType,>(
  key: keyof ItemType,
  setSortType: (value: React.SetStateAction<TableSortType<ItemType> | undefined>) => void
) => {
  return (direction: 'ascending' | 'descending') => {
    const type: TableSortType<ItemType> = {
      predicate: stringSort<ItemType, keyof ItemType & string>(key as keyof ItemType & string),
      sortId: key,
      direction: direction,
    };
    setSortType(type);
  };
};

export const arrayLengthSortCallback = <ItemType,>(
  key: keyof ItemType,
  setSortType: (value: React.SetStateAction<TableSortType<ItemType> | undefined>) => void
) => {
  return (direction: 'ascending' | 'descending') => {
    const type: TableSortType<ItemType> = {
      predicate: arrayLengthSort<ItemType, keyof ItemType & string>(key as keyof ItemType & string),
      sortId: key,
      direction: direction,
    };
    setSortType(type);
  };
};

export const dateSortCallback = <ItemType,>(
  key: keyof ItemType,
  setSortType: (value: React.SetStateAction<TableSortType<ItemType> | undefined>) => void
) => {
  return (direction: 'ascending' | 'descending') => {
    const type: TableSortType<ItemType> = {
      predicate: dateSort<ItemType, keyof ItemType & string>(key as keyof ItemType & string),
      sortId: key,
      direction: direction,
    };
    setSortType(type);
  };
};

export const stringSort = <T, K extends keyof T & string>(key: K) => {
  return (items: T[], direction: 'ascending' | 'descending') => {
    return items.sort((a: T, b: T) => {
      if (a[key] === undefined && b[key] === undefined) {
        return 0;
      }

      if (a[key] === undefined) {
        return 1;
      }

      if (b[key] === undefined) {
        return -1;
      }

      const comparison = (a[key] as string).localeCompare(b[key] as string);
      return direction === 'ascending' ? comparison : -comparison;
    });
  };
};

export const booleanSort = <T, K extends keyof T>(key: K) => {
  return (items: T[], direction: 'ascending' | 'descending') => {
    return items.sort((a: T, b: T) => {
      const comparison = (b[key] === true ? 1 : 0) - (a[key] === true ? 1 : 0);
      return direction === 'ascending' ? comparison : -comparison;
    });
  };
};

export const numberSort = <T, K extends keyof T>(key: K) => {
  return (items: T[], direction: 'ascending' | 'descending') => {
    if (direction === 'ascending') {
      return items.sort((a: T, b: T) => {
        if (a[key] === undefined && b[key] === undefined) {
          return 0;
        }

        if (a[key] === undefined) {
          return 1;
        }

        if (b[key] === undefined) {
          return -1;
        }

        return (a[key] as number) - (b[key] as number);
      });
    }

    return items.sort((a: T, b: T) => {
      if (a[key] === undefined && b[key] === undefined) {
        return 0;
      }

      if (a[key] === undefined) {
        return 1;
      }

      if (b[key] === undefined) {
        return -1;
      }

      return -((a[key] as number) - (b[key] as number));
    });
  };
};

export const arrayLengthSort = <T, K extends keyof T>(key: K) => {
  return (items: T[], direction: 'ascending' | 'descending') => {
    return items.sort((a: T, b: T) => {
      const comparison = (a[key] as unknown[]).length - (b[key] as unknown[]).length;
      return direction === 'ascending' ? comparison : -comparison;
    });
  };
};

export const dateSort = <T, K extends keyof T>(key: K) => {
  return (items: T[], direction: 'ascending' | 'descending') => {
    if (direction === 'ascending') {
      return items.sort((a: T, b: T) => {
        if (a[key] === undefined && b[key] === undefined) {
          return 0;
        }

        if (a[key] === undefined) {
          return 1;
        }

        if (b[key] === undefined) {
          return -1;
        }

        return (a[key] as Date).getTime() - (b[key] as Date).getTime();
      });
    }

    return items.sort((a: T, b: T) => {
      if (a[key] === undefined && b[key] === undefined) {
        return 0;
      }

      if (a[key] === undefined) {
        return 1;
      }

      if (b[key] === undefined) {
        return -1;
      }

      return -((a[key] as Date).getTime() - (b[key] as Date).getTime());
    });
  };
};

export const isInBounds = (value: number | undefined, bounds: [number, number]) => {
  if (value === undefined) {
    return false;
  }

  if (value < bounds[0]) {
    return false;
  }

  if (value > bounds[1]) {
    return false;
  }

  return true;
};

export const isInDateRange = (value: Date | undefined, bounds: [Date, Date]) => {
  if (value === undefined) {
    return false;
  }

  if (bounds[0] === undefined || bounds[1] === undefined) {
    return true;
  }

  if (value.getTime() < bounds[0].getTime()) {
    return false;
  }

  if (value.getTime() > bounds[1].getTime()) {
    return false;
  }

  return true;
};

export const minAndMax = (values: number[]) => {
  return [min(values), max(values)];
};

export const filterItems = <ItemType, FilterStateType>(
  items: ItemType[],
  filterState: FilterStateType,
  filters: Record<
    string,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (source: ItemType) => boolean;
      name: string;
    }
  >,
  searchQuery: string,
  searchKey: keyof ItemType,
  setFilteredItems: (items: ItemType[]) => void,
  sortType: TableSortType<ItemType> | undefined
) => {
  const activeFilters = Object.values(filters).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = items.filter((filterableItem) => {
    let removeItem = false;

    activeFilters.forEach((filter) => {
      if (!filter.filterCallback(filterableItem)) {
        removeItem = true;
      }
    });

    if (searchQuery) {
      if (!(filterableItem[searchKey] as string).toLowerCase().includes(searchQuery.toLowerCase())) {
        removeItem = true;
      }
    }

    return !removeItem;
  });
  if (sortType !== undefined) {
    setFilteredItems(sortItems(filteredResults, sortType) ?? ([] as ItemType[]));
  } else {
    setFilteredItems(filteredResults);
  }
};

export const sortItems = <ItemType,>(filteredResults: ItemType[], sortType: TableSortType<ItemType>) => {
  return sortType && sortType.predicate ? sortType.predicate(filteredResults, sortType.direction) : filteredResults;
};

export const getActiveFilters = <FilterStateType,>(
  defaultFilterState: FilterStateType,
  filterState: FilterStateType
) => {
  return (
    Object.entries(defaultFilterState as Record<string, unknown>).reduce(
      (acc, [key, value]) => acc + (!isEqual(value, filterState[key as keyof FilterStateType]) ? 1 : 0),
      0
    ) > 0
  );
};

export const getActiveFilterCount = <FilterStateType,>(
  defaultFilterState: FilterStateType,
  filterState: FilterStateType
) => {
  return sum(
    Object.entries(defaultFilterState as Record<string, unknown>).map(([key, value]) => {
      return !isEqual(value, filterState[key as keyof FilterStateType]) ? 1 : 0;
    }, 0)
  );
};

export const getFilteredItems = <ItemType,>(
  items: ItemType[],
  activeFilters: {
    filter: React.ReactNode;
    activeCount: () => number;
    filterCallback: (source: ItemType) => boolean;
    name: string;
  }[],
  searchKeys: (keyof ItemType)[],
  searchQuery: string,
  sortType: TableSortType<ItemType> | undefined
) => {
  let filteredResults = items.filter((filterableItem) => {
    let removeItem = false;

    activeFilters.forEach((filter) => {
      if (!filter.filterCallback(filterableItem)) {
        removeItem = true;
      }
    });

    const searchKeyMatches = searchKeys.map((searchKey: keyof ItemType) => {
      if ((filterableItem[searchKey] as string).toLowerCase().includes(searchQuery.toLowerCase())) {
        return true;
      }
      return false;
    });

    if (searchKeyMatches.every((match) => match === false)) {
      removeItem = true;
    }

    return !removeItem;
  });

  if (sortType !== undefined) {
    filteredResults = sortItems(filteredResults, sortType);
  }

  return filteredResults;
};
